import { useContext, useEffect, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useShallow } from "zustand/react/shallow"

import { IFinality } from "./types"
import { Finality } from "./ui"

import { useInput } from "@hooks/useInput"
import { useDebounce } from "@hooks/useDebounce"

import { IFinalityFilter, useFinality } from "@api/finality"

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"
import { CardDeliveryContainer, ICardDeliveryContainer } from "@smart/cardDelivery"
import { IModalConfirm, ModalConfirm } from "@primitive/modal.confirm"
import { CardCompanyContainer, ICardCompanyContainer } from "@smart/cardCompany"
import { CardListenerContainer, ICardListenerContainer } from "@smart/cardListener"
import { CardContractContainer, ICardContractContainer } from "@smart/cardContract"
import { CardApplicationContainer, ICardApplicationContainer } from "@smart/cardApplication"
import { CardDeliverySendPaymentContainer, ICardDeliverySendPaymentContainer } from "@smart/cardDeliverySendPayment"
import { useContracts } from "@api/contracts/state"


/** Контейнер страницы - Доставка */
export const FinalityContainer = () => {
    const { contractID } = useParams()
    const navigate = useNavigate()

    const notify = useContext(NotificationContext)

    const finalityState = useFinality(useShallow((state) => state))
    const { 
        sendedContractStatementDelivery
    } = useContracts(useShallow((state) => ({
        sendedContractStatementDelivery: state.actionSetSendedContractStatementDelivery,
    })))    
    
    const modalConfirm = useInput<IModalConfirm>({isOpen: false})
    const cardDelivery = useInput<ICardDeliveryContainer>({isOpen: false})
    const cardCompanies = useInput<ICardCompanyContainer>({isOpen: false})
    const cardListener = useInput<ICardListenerContainer>({isOpen: false})
    const cardContract = useInput<ICardContractContainer>({isOpen: false})
    const cardApplications = useInput<ICardApplicationContainer>({isOpen: false})
    const modalSendPayment = useInput<ICardDeliverySendPaymentContainer>({isOpen: false})
    const isTelegramSendInProgressRef = useRef(false)

    const txtSearch = useInput<string>('')
    const currentPageNumber = useInput<number>(1)

    useEffect(() => {
        if (contractID) {
            handleOpenCardDelivery(true, Number(contractID))
        }
    }, [contractID]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return (() => {
            finalityState.clearFinalityState()

            currentPageNumber.setValue(1)
            txtSearch.setValue('')
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (finalityState.errors) return notify && notifyOpen(finalityState.errors?.message, finalityState.errors?.type, 3000, notify)
    }, [finalityState.errors]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadPrimaryData(txtSearch.value)
    }, [currentPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps

    /** Перезагрузка списка при изменении reloadTick (закрытие/сохранение договора) */
    useEffect(() => {
        if (finalityState.reloadTick > 0) {
            loadPrimaryData(txtSearch.value)
        }
    }, [finalityState.reloadTick]) // eslint-disable-line react-hooks/exhaustive-deps


    /** Функция по получению списка договоров */
    const loadPrimaryData = (search?: string) => {
        const filterData: IFinalityFilter = {
        }

        finalityState.actionGetFinalityList(currentPageNumber.value, search || '', filterData)
    }
    

    /** Открытие/закрытие окна подтверждения */
    const handlerOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string, actionName?: string, recordID?: number, channelName?: string) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText, 
            handleButtonCancel() {modalConfirm.setValue({isOpen: false})}, 
            async handleButtonOk() {
                if (isTelegramSendInProgressRef.current) return

                isTelegramSendInProgressRef.current = true
                modalConfirm.setValue((currentValue) => ({...currentValue, isLoading: true}))

                try {
                    let status = 0

                    if (actionName === 'sendingApplicationDeliveryToClient') {
                        status = await finalityState.actionTelegramApplicationDeliveryToClient(Number(recordID))
                    } else if (actionName === 'sendingContractStatementDelivery') {
                        status = await sendedContractStatementDelivery(Number(recordID), 1)
                    }

                    if (status === 200) {
                        notify && notifyOpen(`Вся информация успешно отправлена в telegram канал 👉 ${channelName || 'Срочно'}`, 'success', 1500, notify)
                    }
                } finally {
                    isTelegramSendInProgressRef.current = false
                    modalConfirm.setValue({isOpen: false})
                }
            }
        })
    }


    /** Закрытие доставки (с проверкой трек-номера) */
    const handleCloseDelivery = (contractID: number, contractNumber: string, trackNumber: string) => {
        const hasTrack = trackNumber && trackNumber.trim().length > 0

        const headerText = hasTrack
            ? '📋 Закрытие договора'
            : '⚠️ Трек-номер отсутствует'

        const content = hasTrack
            ? <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                <div style={{padding: '12px 14px', borderRadius: '10px', background: 'linear-gradient(135deg,rgba(91,140,255,.12) 0%,rgba(91,140,255,.04) 100%)', border: '1px solid rgba(91,140,255,.22)'}}>
                    <div style={{fontSize: '13px', fontWeight: 700, color: '#5b8cff', marginBottom: '6px'}}>📦 Договор № {contractNumber}</div>
                    <div style={{fontSize: '13px', lineHeight: 1.5, color: 'var(--foreground_default)'}}>
                        Вы действительно хотите <b>закрыть</b> данный договор?<br />
                        Информация будет отправлена в telegram-канал <b>ИКТ — Доставка</b>.
                    </div>
                </div>
                <div style={{padding: '8px 12px', borderRadius: '8px', background: 'rgba(26,138,63,.08)', border: '1px solid rgba(26,138,63,.16)', fontSize: '12px', color: '#1a8a3f', lineHeight: 1.5}}>
                    ✅ Трек-номер: <b>{trackNumber}</b> — будет прикреплён к уведомлению
                </div>
            </div>
            : <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                <div style={{padding: '12px 14px', borderRadius: '10px', background: 'linear-gradient(135deg,rgba(209,50,50,.12) 0%,rgba(209,50,50,.04) 100%)', border: '1px solid rgba(209,50,50,.28)'}}>
                    <div style={{fontSize: '13px', fontWeight: 800, color: '#d13232', marginBottom: '6px'}}>⚠️ Внимание: трек-номер не указан</div>
                    <div style={{fontSize: '13px', lineHeight: 1.5, color: 'var(--foreground_default)'}}>
                        У данной анкеты <b>отсутствует</b> трек-номер для отслеживания доставки.<br />
                        Вы действительно хотите закрыть <b>договор № {contractNumber}</b> без трек-номера?
                    </div>
                </div>
                <div style={{padding: '8px 12px', borderRadius: '8px', background: 'rgba(209,50,50,.06)', border: '1px solid rgba(209,50,50,.14)', fontSize: '12px', color: '#d13232', lineHeight: 1.5}}>
                    ❌ Без трек-номера отслеживание доставки будет недоступно
                </div>
            </div>

        modalConfirm.setValue({
            isOpen: true,
            headerText,
            content,
            handleButtonCancel() { modalConfirm.setValue({ isOpen: false }) },
            handleButtonOk() {
                finalityState.actionTelegramFinalitySendingEndCloseContract(contractID, hasTrack ? trackNumber : '').then((res) => {
                    if (res === 200) {
                        notify && notifyOpen('Договор успешно завершён, информация отправлена в telegram-канал 👉 ИКТ - Доставка', 'success', 1500, notify)
                    }
                })

                modalConfirm.setValue({ isOpen: false })
            }
        })
    }


    /** Копирование информации об анкете в буфер обмена */
    const handleCopyToClipboard = (item: {
        contractNumber: string
        applicationID: number
        contractTypeName: string
        lastName: string
        firstName: string
        secondName: string
        phoneNumber: string
        email: string
        deliveryToWhom: string
        deliveryAddress: string
    }) => {
        const fullName = `${item.lastName} ${item.firstName || ''} ${item.secondName || ''}`.trim()
        const text = [
            `Договор: ${item.contractNumber}`,
            `Заявка: ${item.applicationID}`,
            `Тип договора: ${item.contractTypeName}`,
            `~~~~`,
            `Слушатель: ${fullName}`,
            `Телефон: ${item.phoneNumber}`,
            ...(item.email ? [`Email: ${item.email}`] : []),
            `~~~~`,
            `Получатель: ${item.deliveryToWhom || '—'}`,
            `Адрес: ${item.deliveryAddress || '—'}`,
        ].join('\n')

        navigator.clipboard.writeText(text).then(() => {
            notify && notifyOpen('Текст успешно скопирован в буфер обмена', 'success', 1500, notify)
        }).catch(() => {
            notify && notifyOpen('Не удалось скопировать текст в буфер обмена', 'warning', 2000, notify)
        })
    }


    /** Открытие/закрытие анкеты доставки */
    const handleOpenCardDelivery = (isOpen: boolean, cardID?: number) => {
        cardDelivery.setValue({
            isOpen, cardID,
            handlerButtonCancel() {
                cardDelivery.setValue({isOpen: false})
                navigate('/lk/finality', { replace: true })
            }, 
            handlerButtonOk() {
                cardDelivery.setValue({isOpen: false})
                loadPrimaryData(txtSearch.value)
                navigate('/lk/finality', { replace: true })
            }
        })
    }
    /** Открытие/закрытие карточки по контрагенту */
    const handleOpenCardCompany = (isOpen: boolean, cardID?: number) => {
        cardCompanies.setValue({
            isOpen, cardMode: "edit", cardID,
            handlerButtonCancel() {cardCompanies.setValue({isOpen: false})}, 
            handlerButtonOk() {
                cardCompanies.setValue({isOpen: false})
            }
        })
    }
    /** Открытие/закрытие карточки по слушателю */
    const handleOpenCardListener = (isOpen: boolean, cardID?: number) => {
        cardListener.setValue({
            isOpen, cardMode: "edit", cardID,
            handlerButtonCancel() {cardListener.setValue({isOpen: false})}, 
            handlerButtonOk() {
                cardListener.setValue({isOpen: false})
            }
        })
    }
    /** Открытие/закрытие анкеты по договору */
    const handleOpenCardContract = (isOpen: boolean, cardID?: number) => {
        cardContract.setValue({
            isOpen, cardMode: "edit", cardID,
            handlerButtonCancel() {cardContract.setValue({isOpen: false})}, 
            handlerButtonOk() {
                cardContract.setValue({isOpen: false})
            }
        })
    }
    /** Открытие/закрытие анкеты заявки */
    const handleOpenCardApplication = (isOpen: boolean, cardID?: number) => {
        cardApplications.setValue({
            isOpen, cardMode: "edit", cardID,
            handleButtonCancel() {cardApplications.setValue({isOpen: false})}, 
            handleButtonOk() {
                cardApplications.setValue({isOpen: false})
            }
        })
    }
    /** Открытие/закрытие окна отправки счёта в telegram */
    const handleOpenModalSendPayment = (isOpen: boolean, contractID: number, contractTypeID: number) => {
        modalSendPayment.setValue({
            isOpen, cardID: contractID, contractTypeID,
            handleBtnCancel() { modalSendPayment.setValue({ isOpen: false }) },
            handleBtnOk() {

                
                modalSendPayment.setValue({ isOpen: false })
            }
        })
    }


    const debounceSearch = useDebounce(loadPrimaryData, 1500) // Запуск загрузки заявок через полторы секунды
    const txtSearchOnChange = (value: string) => {txtSearch.setValue(value); debounceSearch(value)} // Поле поиска

    /** Свойства передаваемые в компоненту */
    const propsToComponent: IFinality = {
        isLoading: finalityState.contracts.isLoading,
        currentPageNumber, totalCount: finalityState.contracts.totalCount, pageSize: finalityState.contracts.pageSize,

        txtSearch, primaryList: finalityState.contracts.items,

        loadPrimaryData, txtSearchOnChange,
        handleOpenCardDelivery, handlerOpenModalConfirm, handleCloseDelivery, handleCopyToClipboard, handleOpenCardCompany, 
        handleOpenCardListener, handleOpenCardContract, handleOpenCardApplication, handleOpenModalSendPayment
    }

    return <>
        <Finality {...propsToComponent} />
        
        <ModalConfirm {...modalConfirm.value} />
        <CardDeliveryContainer {...cardDelivery.value} />
        <CardCompanyContainer {...cardCompanies.value} />
        <CardListenerContainer {...cardListener.value} />
        <CardContractContainer {...cardContract.value} />
        <CardApplicationContainer {...cardApplications.value} />
        <CardDeliverySendPaymentContainer {...modalSendPayment.value} />
    </>
}